import { Box, FormHelperText, FormLabel, useTheme } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { TextInput, type TextInputProps } from "./TextInput";

export interface FormTextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<TextInputProps, "onChange" | "value"> {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
}

export const FormTextInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  required,
  ...props
}: FormTextInputProps<TFieldValues, TName>) => {
  const { colors, customSpacing, typography, customFontWeight } = useTheme();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: customSpacing.sm,
          }}
        >
          {label && (
            <FormLabel
              htmlFor={props.id || name}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: typography.body2.fontSize,
                fontWeight: customFontWeight.medium,
                color: colors.text.primary,
                "&.Mui-focused": { color: colors.text.primary },
                "&.Mui-error": { color: colors.text.primary },
              }}
            >
              {label}
              {required && (
                <span
                  style={{
                    color: colors.text.danger,
                    fontSize: typography.body2.fontSize,
                    fontWeight: typography.body2.fontWeight,
                  }}
                >
                  *
                </span>
              )}
            </FormLabel>
          )}
          <TextInput
            {...props}
            {...field}
            id={props.id || name}
            onChange={(value: string) => field.onChange(value)}
            error={!!error}
            sx={{
              boxShadow: error ? `0 0 0 2px ${colors.border.danger}` : "none",
            }}
          />
          {error && (
            <FormHelperText sx={{ color: colors.text.danger }}>
              {error.message}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
};
